"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsGetKeywordDetails = exports.googleAdsGetKeywordDetailsResultSchema = exports.googleAdsGetKeywordDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var googleAdsGetKeywordDetailsInputSchema = zod_1.z
    .object({
    customerId: zod_1.z
        .string()
        .nullish()
        .describe('ONLY provide if user explicitly specifies a Google Ads customer ID. DO NOT generate or guess customer IDs. Leave empty to automatically use the selected account. Must be provided by user - never invent random numbers.'),
    campaignId: zod_1.z
        .string()
        .nullish()
        .describe('Specific campaign ID to analyze. If not provided, returns data for all campaigns in the account.'),
    query: zod_1.z
        .string()
        .nullish()
        .describe('Optional: Keyword search text to filter your active keywords (e.g., "shoes" finds "running shoes", "red shoes"). Leave empty to return ALL keywords you are bidding on. When provided, searches keywords containing this text.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-08-01. If omitted, returns lifetime data from keyword creation.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-08-31. If omitted, returns data up to today.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of keyword results to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    failIfEmpty: zod_1.z
        .boolean()
        .default(true)
        .describe('Fail if the search returns no results. Unless specified, defaults to true.'),
})
    .describe('Analyze performance of keywords you are currently bidding on in your Google Ads campaigns.');
exports.googleAdsGetKeywordDetailsArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(googleAdsGetKeywordDetailsInputSchema);
var googleAdsGetKeywordDetailsResponseSchema = zod_1.z.array(zod_1.z.object({
    keyword: zod_1.z.string().describe('The keyword text you are bidding on'),
    matchType: zod_1.z.enum(['BROAD', 'PHRASE', 'EXACT']).nullish().describe('Match type of the keyword'),
    status: zod_1.z.string().nullish().describe('Keyword status (e.g., ENABLED, PAUSED, REMOVED)'),
    adGroup: zod_1.z.string().nullish().describe('Ad group name'),
    campaign: zod_1.z.string().nullish().describe('Campaign name'),
    currencyCode: zod_1.z
        .string()
        .nullish()
        .describe('Currency code for cost metrics (e.g., USD, EUR, GBP)'),
    metrics: zod_1.z.object({
        impressions: zod_1.z.number().describe('Number of impressions in last 30 days'),
        clicks: zod_1.z.number().describe('Number of clicks in last 30 days'),
        ctr: zod_1.z.number().describe('Click-through rate (CTR)'),
        averageCpc: zod_1.z.number().describe('Average cost per click in account currency'),
        cost: zod_1.z.number().describe('Total cost in account currency in last 30 days'),
        conversions: zod_1.z.number().describe('Number of conversions in last 30 days'),
        conversionValue: zod_1.z
            .number()
            .nullish()
            .describe('Total conversion value in account currency (if conversion value tracking is enabled)'),
        valuePerConversion: zod_1.z
            .number()
            .nullish()
            .describe('Average value per conversion in account currency'),
        roas: zod_1.z
            .number()
            .nullish()
            .describe('Return on ad spend (ROAS) - calculated as conversion_value / cost'),
        searchImpressionShare: zod_1.z
            .number()
            .min(0)
            .max(1)
            .nullish()
            .describe('Search impression share (0-1, where 1 = 100%) - percentage of eligible impressions your ads received'),
        averagePosition: zod_1.z
            .number()
            .nullish()
            .describe('Average position of ads on search results page (lower is better, 1 = top position)'),
        qualityScore: zod_1.z.number().min(1).max(10).nullish().describe('Google Ads quality score (1-10)'),
    }),
}));
exports.googleAdsGetKeywordDetailsResultSchema = googleAdsGetKeywordDetailsResponseSchema;
exports.googleAdsGetKeywordDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'googleAdsGetKeywordDetails',
    displayName: 'Google Ads Campaign Keyword Performance',
    displayDescription: 'Get performance data for keywords you are currently bidding on in your Google Ads account. Optionally filter by campaign ID, keyword search query, or date range. Shows impressions, clicks, CTR, CPC, cost, conversions, conversion value, ROAS, search impression share, average position, quality scores, and keyword status. Automatically uses the selected account unless user provides specific customer ID.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsGetKeywordDetailsArgsSchema,
}, googleAdsGetKeywordDetailsResponseSchema);
